/*
 * kp-film.js — the looping-film player, shared (#47).
 *
 *   <kp-film>  One or more muted loops under one pause control (a pair
 *              repeats that control in each well, and every copy shows the
 *              same state). Autoplay is decided in JS so
 *              prefers-reduced-motion can veto it before a byte is fetched,
 *              an IntersectionObserver starts the loops on arrival and
 *              pauses them off screen, and aria-pressed on the button is the
 *              only state.
 *
 * Lifted out of kp-tech-page.js (#40), where it was <kp-tech-film>, when the
 * chapter collection hero needed the same player. Nothing about the behaviour
 * changed in the move — only the element name, the data attributes
 * (data-kp-tp-* → data-kp-film-*) and the file it lives in.
 *
 * Films warm one screen ahead (#43). Callers ship preload="none", which is
 * right — a 1080p film fetching metadata above the fold is #26 — but "none"
 * also means a film starts downloading at the moment it is asked to play,
 * and stalls on its first frame. The near observer promotes them to
 * preload="metadata" while they are still a screenful away, so the bytes and
 * the arrival are not the same instant.
 *
 * A caller with a landscape cut AND a portrait one hands them over as two
 * <template data-kp-film-video="landscape|portrait"> children instead of two
 * <video>s; hydrate() keeps the one the viewport asks for and drops the
 * other before anything is fetched. See the note on that method for why not
 * <source media>.
 *
 * THE TOGGLE IS REQUIRED. With no [data-kp-film-toggle] inside it this
 * element does nothing at all — deliberately: a loop that runs over other
 * content with no way to stop it is SC 2.2.2 failed, so there is no silent
 * path to one. A caller that does not want to SEE the button hides it with
 * CSS (opacity, never display:none) and leaves it in the accessibility tree;
 * kp-collection-hero does exactly that.
 *
 * The helpers are published on window.KpFilm because kp-tech-page.js's hit
 * test uses them too. That makes the load order a contract: every section
 * that tags kp-tech-page.js must tag THIS file first. Both are deferred, so
 * document order is execution order.
 *
 * Wrapped, because every kp-*.js is a classic <script src> and several
 * sections on one page can tag this file: a bare top-level const would throw
 * "already declared" on the second pass (ticket #22). customElements.get()
 * keeps the define idempotent.
 */
(function () {
  const KP_FILM_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const KP_FILM_STACK = window.matchMedia('(max-width: 749px)');

  /* One viewport of lead time. Matches the PDP bands and the chapter stages:
   * far enough that the film has metadata by the time it is asked to run,
   * near enough that a visitor who never scrolls there pays nothing. */
  const KP_FILM_NEAR = '100% 0px';

  function swallow(promise) {
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
  }

  /* preload="none" → "metadata". One way only: a film that has been warmed
   * stays warm, because un-warming it would not give the bytes back. */
  function warm(videos) {
    videos.forEach((video) => {
      if (video.preload === 'none') video.preload = 'metadata';
    });
  }

  /* The observer every player uses to warm its films. Returns null where
   * IntersectionObserver is missing, and the caller warms immediately —
   * a browser that old is not the one we are saving bytes on. */
  function nearObserver(host, videos) {
    if (!('IntersectionObserver' in window)) {
      warm(videos);
      return null;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        warm(videos);
        observer.disconnect();
      },
      { rootMargin: KP_FILM_NEAR }
    );
    observer.observe(host);
    return observer;
  }

  /* ── Looping films under one control ─────────────────────────────── */

  class KpFilm extends HTMLElement {
    connectedCallback() {
      this.hydrate();
      this.videos = Array.from(this.querySelectorAll('video'));
      /* Plural: the stacked pair puts a control in each well, and both are
       * the same control. Whichever is pressed, all of them repaint. */
      this.toggles = Array.from(this.querySelectorAll('[data-kp-film-toggle]'));
      this.labels = Array.from(this.querySelectorAll('[data-kp-film-label]'));
      if (!this.videos.length || !this.toggles.length) return;

      /* A cluster — the dead-feel / good-feel pair. Side by side on a desktop
       * the two running together IS the comparison. Stacked on a phone they
       * are never both on screen, so running both is one 1080p stream paid
       * for nothing, and it breaks the page's one-thing-moving rule. Below
       * --kp-bp-stack only the film in view runs. */
      this.cluster = this.videos.length > 1;
      this.onScreen = new Set();

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.onToggle(), { signal });
      });
      this.videos.forEach((video) => {
        video.addEventListener('play', () => this.sync(), { signal });
        video.addEventListener('pause', () => this.sync(), { signal });
        // A cloned or server-rendered <video> can reach the document with
        // muted unset, and an unmuted autoplay is refused.
        video.muted = true;
      });

      // The customer's own pause beats the observer: someone who stopped the
      // films and scrolled back should find them still stopped. Under
      // reduced motion the films never start on their own, so "by hand" is
      // the starting state and the button is the way in.
      this.pausedByHand = KP_FILM_REDUCED.matches;
      this.sync();
      this.watch();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      [this.observer, this.near, this.soloObserver].forEach((o) => {
        if (o) o.disconnect();
      });
    }

    /*
     * Two assets, two <video>s — the shape kp-core-structure and kp-pdp-tech
     * already use. A landscape cut and a portrait one are different films,
     * not renditions of one, and <source media> is the wrong tool for them:
     * it is evaluated once at load and never again, so a rotate leaves the
     * wrong film playing, and both files sit in the markup either way.
     *
     * Here the pick happens once, from the viewport, and the loser's
     * <template> is dropped without ever having been parsed as media — so a
     * phone does not even hold a reference to the wide cut. Rotating keeps
     * the film that is running rather than refetching a clip mid-read.
     *
     * An element with no templates is untouched: the tech page's films are
     * plain <video> children and always were.
     */
    hydrate() {
      const templates = Array.from(this.querySelectorAll('[data-kp-film-video]'));
      if (!templates.length) return;

      const wanted = KP_FILM_STACK.matches ? 'portrait' : 'landscape';
      const pick =
        templates.find((t) => (t.dataset.kpFilmVideo || 'landscape') === wanted) || templates[0];

      templates.forEach((t) => {
        if (t === pick) t.parentNode.insertBefore(t.content.cloneNode(true), t);
        t.remove();
      });
    }

    watch() {
      this.near = nearObserver(this, this.videos);

      if (!('IntersectionObserver' in window)) {
        if (!this.pausedByHand) this.play();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.inView = true;
              if (!this.pausedByHand) this.play();
            } else {
              this.inView = false;
              this.pause();
            }
          });
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this);

      if (!this.cluster) return;

      /* Which film of the pair is being looked at. Half of a well visible is
       * the reading of "this one" the chapter stages use, for the same
       * reason: a well passing through the corner of the screen is not the
       * one you are watching. */
      this.soloObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.onScreen.add(entry.target);
            else this.onScreen.delete(entry.target);
          });
          if (this.inView && !this.pausedByHand) this.play();
        },
        { threshold: 0.5 }
      );
      this.videos.forEach((video) => this.soloObserver.observe(video));
    }

    onToggle() {
      if (this.playing()) {
        this.pausedByHand = true;
        this.pause();
      } else {
        this.pausedByHand = false;
        // A press is a user gesture and the answer must be immediate, so
        // the bytes are wanted now rather than one screen from now.
        warm(this.videos);
        this.play();
      }
    }

    playing() {
      return this.videos.some((video) => !video.paused);
    }

    /* Solo only applies to a cluster on a phone. Everywhere else — the hero's
     * single film, the pair side by side — every film in the element runs. */
    solo() {
      return this.cluster && KP_FILM_STACK.matches;
    }

    play() {
      /* Mid-seam on a phone neither well is half visible, and a press then
       * would start nothing at all. One film is the rule, so pick the first
       * rather than none. */
      const solo = this.solo();
      const chosen = solo && this.onScreen.size === 0 ? this.videos[0] : null;
      this.videos.forEach((video) => {
        if (solo && video !== chosen && !this.onScreen.has(video)) {
          video.pause();
          return;
        }
        swallow(video.play());
      });
    }

    pause() {
      this.videos.forEach((video) => video.pause());
    }

    sync() {
      const playing = this.playing();
      this.toggles.forEach((toggle) => {
        toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      });
      this.labels.forEach((label, i) => {
        const toggle = this.toggles[i] || this.toggles[0];
        label.textContent = playing
          ? toggle.dataset.pauseLabel || ''
          : toggle.dataset.playLabel || '';
      });
    }
  }

  /* Published for kp-tech-page.js — see the load-order note at the top. */
  window.KpFilm = {
    swallow: swallow,
    warm: warm,
    nearObserver: nearObserver,
    reduced: KP_FILM_REDUCED,
    stack: KP_FILM_STACK
  };

  if (!customElements.get('kp-film')) customElements.define('kp-film', KpFilm);

})();
